/**
 * Cascading Variants for dbtfy/Turbo-style themes
 * ------------------------------------------------------------------
 * Makes multi-level Shopify variant options cascade:
 *   option1 (e.g. Category)  -> filters option2 (e.g. Subcategory)
 *   option2                  -> filters option3 (e.g. Size)
 *
 * Generic & zero hard-coding: it reads every valid combination from the
 * master <select> <option data-option-1/2/3> attributes that the theme
 * already renders, so it works on ANY product with 2-3 variant options.
 *
 * Works with the radio variant selector (product_selector == 'radio'),
 * which renders <input type="radio" name="optionN" data-index="optionN">.
 *
 * Safe to load on every page: it no-ops when there is no 3-option
 * variant form on the page.
 */
(function () {
  'use strict';

  function init() {
    // Each product form has its own master <select id="MainProductSelect-{id}">.
    var masterSelects = document.querySelectorAll('select.product-single__variants[id^="MainProductSelect-"]');

    masterSelects.forEach(function (masterSelect) {
      // Avoid double-init if a section re-renders.
      if (masterSelect.dataset.cascadingInit === '1') return;
      masterSelect.dataset.cascadingInit = '1';

      var form = masterSelect.closest('form');
      if (!form) return;

      // Only engage when the product has 3 variant options. 1- or 2-option
      // products are left untouched.
      var optCount = countOptions(form);
      if (optCount < 3) return;

      setupCascading(form, masterSelect);
    });
  }

  function countOptions(form) {
    // option groups share the name "option1", "option2", "option3"
    var names = {};
    form.querySelectorAll('[name^="option"]').forEach(function (el) {
      names[el.name] = true;
    });
    return Object.keys(names).length;
  }

  function setupCascading(form, masterSelect) {
    // Build the list of valid (option1, option2, option3) tuples from the
    // master select. Only available variants are selectable, but we keep
    // sold-out ones too so we can grey them out instead of hiding them.
    var combos = [];
    masterSelect.querySelectorAll('option[data-option-1]').forEach(function (opt) {
      combos.push({
        id: opt.value,
        available: !opt.disabled,
        o1: opt.getAttribute('data-option-1'),
        o2: opt.getAttribute('data-option-2'),
        o3: opt.getAttribute('data-option-3'),
      });
    });

    var urlTimer = null;

    var opt1Radios = radiosFor(form, 'option1');
    var opt2Radios = radiosFor(form, 'option2');
    var opt3Radios = radiosFor(form, 'option3');

    // For each radio, remember its visible <label for="..."> sibling so we
    // can hide/show them together without restructuring the DOM (the theme
    // renders <input> + <label> as siblings inside the <fieldset>).
    opt1Radios.forEach(cacheLabel);
    opt2Radios.forEach(cacheLabel);
    opt3Radios.forEach(cacheLabel);

    // Cache each option group's outer wrapper (.radio-wrapper) so we can
    // hide an entire option group when it only has a single selectable
    // value (no point making the customer pick the only option).
    opt1Radios.forEach(cacheWrapper);
    opt2Radios.forEach(cacheWrapper);
    opt3Radios.forEach(cacheWrapper);

    // ---- cascading rules -------------------------------------------------

    function opt2ValuesFor(o1) {
      var seen = {};
      combos.forEach(function (c) {
        if (c.o1 === o1 && !seen[c.o2]) seen[c.o2] = true;
      });
      return Object.keys(seen);
    }

    function opt3ValuesFor(o1, o2) {
      var seen = {};
      combos.forEach(function (c) {
        if (c.o1 === o1 && c.o2 === o2 && !seen[c.o3]) seen[c.o3] = true;
      });
      return Object.keys(seen);
    }

    function isComboAvailable(o1, o2, o3) {
      return combos.some(function (c) {
        return c.o1 === o1 && c.o2 === o2 && c.o3 === o3 && c.available;
      });
    }

    // ---- DOM helpers -----------------------------------------------------

    function filterRadios(radios, allowedValues) {
      var firstEnabled = null;
      radios.forEach(function (radio) {
        var allowed = allowedValues.indexOf(radio.value) !== -1;
        // Toggle the radio + its label together, no DOM restructuring.
        radio.style.display = allowed ? '' : 'none';
        if (radio.__label) radio.__label.style.display = allowed ? '' : 'none';

        if (!allowed) {
          radio.checked = false;
          radio.disabled = true;
        } else {
          radio.disabled = false;
          if (!firstEnabled) firstEnabled = radio;
        }
      });
      return firstEnabled;
    }

    // Hide an option group's whole wrapper when only a single value is
    // selectable (the choice is meaningless). Restores it when more than
    // one value becomes available again after a cascade change.
    function toggleSingleValueOption(radios) {
      var visibleCount = 0;
      radios.forEach(function (radio) {
        if (radio.style.display !== 'none') visibleCount++;
      });
      var wrapper = radios.length ? radios[0].__wrapper : null;
      if (wrapper) {
        // Keep the radio checked so the variant stays valid, just hide
        // the (single-choice) picker from the customer.
        wrapper.style.display = visibleCount <= 1 ? 'none' : '';
      }
    }

    // ---- cascade actions -------------------------------------------------

    function applyCascade(trigger) {
      var o1 = checkedValue(opt1Radios);

      if (trigger === 'option1') {
        var allowed2 = o1 ? opt2ValuesFor(o1) : [];
        var first2 = filterRadios(opt2Radios, allowed2);

        // If the currently-checked option2 is no longer valid, pick the
        // first valid one so the cascade always lands on a real combo.
        if (first2 && (!opt2Radios.some(isChecked) || allowed2.indexOf(checkedValue(opt2Radios)) === -1)) {
          first2.checked = true;
          fireChange(first2);
        } else if (!first2) {
          // No valid option2 for this option1 (shouldn't happen with sane
          // variant data) - just clear option3.
        }
      }

      o1 = checkedValue(opt1Radios);
      var o2 = checkedValue(opt2Radios);
      var allowed3 = (o1 && o2) ? opt3ValuesFor(o1, o2) : [];
      var first3 = filterRadios(opt3Radios, allowed3);

      if (first3 && (!opt3Radios.some(isChecked) || allowed3.indexOf(checkedValue(opt3Radios)) === -1)) {
        first3.checked = true;
        fireChange(first3);
      }

      // After filtering, hide any option group that only has a single
      // selectable value left (e.g. a Category whose only Subcategory
      // makes Subcategory a no-op picker).
      toggleSingleValueOption(opt2Radios);
      toggleSingleValueOption(opt3Radios);

      // Make sure the master <select> reflects the (possibly auto-picked)
      // combination, so price/image/Customily preview stay in sync.
      syncMasterSelect();
    }

    function syncMasterSelect() {
      var o1 = checkedValue(opt1Radios);
      var o2 = checkedValue(opt2Radios);
      var o3 = checkedValue(opt3Radios);

      var match = combos.find(function (c) {
        return c.o1 === o1 && c.o2 === o2 && c.o3 === o3;
      });

      if (match && masterSelect.value !== match.id) {
        masterSelect.value = match.id;
        fireChange(masterSelect);
      }

      // Sync ?variant= in the URL so refresh/back/ share keep the picked
      // variant (migrated from the old inline script in theme.liquid).
      syncUrl(match);
    }

    function syncUrl(match) {
      clearTimeout(urlTimer);
      urlTimer = setTimeout(function () {
        var id = match ? match.id : masterSelect.value;
        if (!id) return;
        try {
          var u = new URL(location.href);
          u.searchParams.set('variant', id);
          history.replaceState(null, '', u);
        } catch (e) { /* URL API unavailable, skip */ }
      }, 80);
    }

    // ---- initial state ---------------------------------------------------

    // On first paint the theme selects the first available variant. We
    // respect that: just hide option2/option3 values that don't belong to
    // the currently-selected option1, without forcing a reselection.
    (function initialFilter() {
      var o1 = checkedValue(opt1Radios) || firstValue(opt1Radios);
      filterRadios(opt2Radios, o1 ? opt2ValuesFor(o1) : []);
      var o2 = checkedValue(opt2Radios) || firstValue(opt2Radios);
      filterRadios(opt3Radios, (o1 && o2) ? opt3ValuesFor(o1, o2) : []);
      // Hide any single-value option group on initial load too.
      toggleSingleValueOption(opt1Radios);
      toggleSingleValueOption(opt2Radios);
      toggleSingleValueOption(opt3Radios);
    })();

    // ---- wire up events --------------------------------------------------

    opt1Radios.forEach(function (radio) {
      radio.addEventListener('change', function () { applyCascade('option1'); });
    });
    opt2Radios.forEach(function (radio) {
      radio.addEventListener('change', function () { applyCascade('option2'); });
    });
    opt3Radios.forEach(function (radio) {
      radio.addEventListener('change', function () { applyCascade('option3'); });
    });
  }

  // ---- tiny utilities ----------------------------------------------------

  function radiosFor(form, optionName) {
    return Array.prototype.slice.call(
      form.querySelectorAll('input.js-single-option-selector[name="' + optionName + '"]')
    );
  }

  function cacheLabel(radio) {
    // The visible swatch label is the <label for="..."> whose `for` matches
    // the radio id. Toggle its display alongside the radio.
    radio.__label = document.querySelector('label[for="' + radio.id + '"]');
  }

  function cacheWrapper(radio) {
    // The option group's outer wrapper holds the label + fieldset of all
    // values for one option (e.g. all Subcategory radios). Hiding it hides
    // the whole picker, used when only one value is selectable.
    radio.__wrapper = radio.closest('.radio-wrapper, .product-form__item');
  }

  function checkedValue(radios) {
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].checked) return radios[i].value;
    }
    return null;
  }

  function firstValue(radios) {
    return radios.length ? radios[0].value : null;
  }

  function isChecked(radio) {
    return radio.checked;
  }

  function fireChange(el) {
    var evt;
    try {
      evt = new Event('change', { bubbles: true });
    } catch (e) {
      evt = document.createEvent('Event');
      evt.initEvent('change', true, true);
    }
    el.dispatchEvent(evt);
  }

  // ---- boot --------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after Shopify section re-renders (e.g. variant selection via
  // Section Rendering API replaces the product form HTML).
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
})();
